import React, { useRef, useEffect, useCallback, useState } from 'react';
import styled from 'styled-components';

import useProgressHeartbeat from './useProgressHeartbeat';

const Wrap = styled.div`
  background: #000;
  border-radius: 6px;
  overflow: hidden;
  position: relative;
  max-width: 960px;
`;

const StyledVideo = styled.video`
  width: 100%;
  display: block;
  max-height: 540px;
`;

/**
 * HTML5 video player with:
 *  - Resume-from-last-position on mount.
 *  - Cumulative WatchedSeconds tracking (hard to cheat by scrubbing).
 *  - Optional DisableFastForward that clamps seeks beyond the furthest
 *    position the user has actually reached.
 *  - 30s heartbeat + end-of-video flush via useProgressHeartbeat.
 */
export default function VideoPlayer({
  contentItem,
  assignmentId,
  disableFastForward = false,
  onCompleted,
  onProgress,
}) {
  const ref = useRef(null);
  const stateRef = useRef({
    watchedSeconds: contentItem.watchedSeconds ?? 0,
    lastPositionSeconds: contentItem.lastPositionSeconds ?? 0,
    maxReachedSeconds: contentItem.lastPositionSeconds ?? 0,
    clientCompleted: contentItem.isCompleted ?? false,
  });
  const lastTickRef = useRef(null);
  const [isCompleted, setIsCompleted] = useState(contentItem.isCompleted ?? false);

  const getState = useCallback(() => ({
    contentItemId: contentItem.id,
    lastPositionSeconds: Math.floor(stateRef.current.lastPositionSeconds),
    watchedSeconds: Math.floor(stateRef.current.watchedSeconds),
    clientCompleted: stateRef.current.clientCompleted,
  }), [contentItem.id]);

  const flush = useProgressHeartbeat(assignmentId, getState, (res) => {
    if (res?.isCompleted) {
      setIsCompleted(true);
      stateRef.current.clientCompleted = true;
    }
    onProgress?.(res);
  });

  // Resume from last known position once the video is ready.
  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    const onLoaded = () => {
      const resume = contentItem.lastPositionSeconds;
      if (resume && resume > 0 && resume < (el.duration - 1)) {
        el.currentTime = resume;
      }
    };
    el.addEventListener('loadedmetadata', onLoaded);
    return () => el.removeEventListener('loadedmetadata', onLoaded);
  }, [contentItem.lastPositionSeconds]);

  const onTimeUpdate = () => {
    const el = ref.current;
    if (!el) return;
    const now = el.currentTime;
    const prev = lastTickRef.current;
    lastTickRef.current = now;

    // Only credit natural forward playback. Seeks (delta > 2s or negative) must
    // not advance maxReachedSeconds — otherwise a forward scrub that fires
    // timeupdate before seeking would defeat the disableFastForward clamp.
    if (prev != null) {
      const delta = now - prev;
      if (delta > 0 && delta < 2) {
        stateRef.current.watchedSeconds += delta;
        if (now > stateRef.current.maxReachedSeconds) {
          stateRef.current.maxReachedSeconds = now;
        }
      }
    }

    stateRef.current.lastPositionSeconds = now;
  };

  const onSeeking = () => {
    if (!disableFastForward) return;
    const el = ref.current;
    if (!el) return;
    const max = stateRef.current.maxReachedSeconds + 1; // +1s tolerance
    if (el.currentTime > max) {
      el.currentTime = max;
    }
  };

  const onEnded = () => {
    stateRef.current.clientCompleted = true;
    setIsCompleted(true);
    flush({ clientCompleted: true }).then(() => onCompleted?.());
  };

  const onPause = () => {
    // Extra flush on pause so tab-closes don't lose the last few seconds.
    flush();
  };

  useEffect(() => () => { flush(); }, [flush]);

  return (
    <Wrap>
      <StyledVideo
        ref={ref}
        src={contentItem.url}
        controls
        controlsList={disableFastForward ? 'nodownload noplaybackrate' : 'nodownload'}
        onTimeUpdate={onTimeUpdate}
        onSeeking={onSeeking}
        onSeeked={onSeeking}
        onEnded={onEnded}
        onPause={onPause}
        preload="metadata"
        data-completed={isCompleted ? 'true' : 'false'}
      />
    </Wrap>
  );
}
